/**
 * CRM Notes API Functions
 *
 * API layer for the cross-entity CRM note feed. Notes attach to a CRM entity
 * (company or contact) and are shown in the central content area of that
 * entity's detail page. Proxied at /api/v4/crm/*.
 */

import { api } from '@/lib/api';

export type CrmNoteEntityType = 'company' | 'contact' | 'opportunity';

/** A single CRM note as returned by the backend (camelCase wire shape). */
export interface CrmNote {
  id: string;
  bodyHtml: string;
  createdByUserId: string;
  createdByUserName: string;
  createdAt: string;
  updatedAt: string;
}

interface V4CrmNoteResponse {
  id: string;
  bodyHtml: string;
  createdByUserId: string;
  createdByUserName: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Transform a V4 note response into the internal shape.
 * Throws if required fields are missing to prevent corrupt data.
 */
function transformNote(note: V4CrmNoteResponse): CrmNote {
  if (!note || typeof note !== 'object') {
    throw new Error('[CRM Notes API] Invalid note response: expected object, got ' + typeof note);
  }

  const missingFields: string[] = [];
  if (!note.id) missingFields.push('id');
  if (typeof note.bodyHtml !== 'string') missingFields.push('bodyHtml');

  if (missingFields.length > 0) {
    throw new Error(`[CRM Notes API] Note response missing required fields: ${missingFields.join(', ')}`);
  }

  return {
    id: note.id,
    bodyHtml: note.bodyHtml,
    createdByUserId: note.createdByUserId ?? '',
    createdByUserName: note.createdByUserName ?? 'Unknown',
    createdAt: note.createdAt ?? new Date().toISOString(),
    updatedAt: note.updatedAt ?? note.createdAt ?? new Date().toISOString(),
  };
}

/**
 * Get all notes for an entity, newest first.
 */
export async function getNotes(
  entityType: CrmNoteEntityType,
  entityId: string,
): Promise<CrmNote[]> {
  const url = `/api/v4/crm/${entityType}/${entityId}/notes`;
  const data = await api.get<{ notes: V4CrmNoteResponse[] }>(url);
  return (data.notes ?? []).map(transformNote);
}

/**
 * Create a note on an entity.
 */
export async function createNote(
  entityType: CrmNoteEntityType,
  entityId: string,
  bodyHtml: string,
): Promise<CrmNote> {
  const url = `/api/v4/crm/${entityType}/${entityId}/notes`;
  const data = await api.post<V4CrmNoteResponse>(url, { bodyHtml });
  return transformNote(data);
}

/**
 * Update a note's body.
 */
export async function updateNote(noteId: string, bodyHtml: string): Promise<CrmNote> {
  const url = `/api/v4/crm/notes/${noteId}`;
  const data = await api.put<V4CrmNoteResponse>(url, { bodyHtml });
  return transformNote(data);
}

/**
 * Delete a note.
 */
export async function deleteNote(noteId: string): Promise<void> {
  await api.delete(`/api/v4/crm/notes/${noteId}`);
}
